/**
 * ONE TAP — persisted player settings + the settings panel.
 *
 * This is the only leaf module under `src/client/ui/` (it imports nothing else of
 * ours), so it also hosts the two micro-helpers every other UI module needs —
 * `esc()` and `teamColor()` — rather than having four copies of them drift apart.
 *
 * Settings that the fixed `UIHooks` surface does not cover (FOV, quality preset,
 * colourblind palette, music/sfx split) are published through `getSettings()` and
 * `onSettingsChange()`. Any other client module — the renderer, the audio engine —
 * can import those directly; the store is the single source of truth and it
 * persists to localStorage on every change.
 */

import {
  FOV_BASE,
  MOUSE_SENSITIVITY_DEFAULT,
  PALETTE,
  TEAM_A,
} from '@shared/constants';

// ---------------------------------------------------------------------------
// Shared micro-helpers (see file header for why they live here)
// ---------------------------------------------------------------------------

/** Escape a string for safe interpolation into an innerHTML template. */
export function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/** Base palette hexes, straight from `PALETTE` in @shared/constants (Sunlit Cel). */
export const PALETTE_HEX = {
  sky: hex(PALETTE.sky),
  skyHigh: hex(PALETTE.skyHigh),
  sun: hex(PALETTE.sun),
  base: hex(PALETTE.base),
  base2: hex(PALETTE.base2),
  shadow: hex(PALETTE.shadow),
  ink: hex(PALETTE.ink),
  teamA: hex(PALETTE.teamA),
  teamB: hex(PALETTE.teamB),
  loaded: hex(PALETTE.loaded),
  empty: hex(PALETTE.empty),
  accentA: hex(PALETTE.accentA),
  accentB: hex(PALETTE.accentB),
  danger: hex(PALETTE.danger),
} as const;

/**
 * Colourblind-safe substitute pair (Okabe–Ito blue / vermillion). Azure vs signal
 * red is a hard read for protan viewers and team identity is load-bearing in this
 * game, so the option swaps the *whole* team hue, UI and world alike. Vermillion
 * is chosen over Okabe–Ito orange because orange would collide with the amber
 * LOADED signal, and "who is armed" must never be ambiguous.
 */
export const CB_TEAM_A = '#0072b2';
export const CB_TEAM_B = '#d55e00';

/** CSS colour for a team, honouring the colourblind preference. */
export function teamColor(team: number): string {
  const cb = state.colorblind;
  if (team === TEAM_A) return cb ? CB_TEAM_A : PALETTE_HEX.teamA;
  return cb ? CB_TEAM_B : PALETTE_HEX.teamB;
}

// ---------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------

export type QualityPreset = 'low' | 'medium' | 'high';

export interface GameSettings {
  /** Last name the player used. Prefills the menu so nobody retypes it. */
  name: string;
  /** Raw radians-per-pixel, exactly what `InputManager.sensitivity` wants. */
  sensitivity: number;
  /** Preferred base FOV in degrees. The renderer widens from here with speed. */
  fov: number;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  quality: QualityPreset;
  colorblind: boolean;
  showFps: boolean;
  /** Opt-out of camera shake for motion-sensitive players. */
  reducedShake: boolean;
}

const STORAGE_KEY = 'onetap.settings.v1';

/** Sensitivity slider is presented as a multiplier of the default; these bound it. */
export const SENS_MULT_MIN = 0.2;
export const SENS_MULT_MAX = 4.0;
export const FOV_MIN = 80;
export const FOV_UI_MAX = 120;

export const DEFAULT_SETTINGS: Readonly<GameSettings> = Object.freeze({
  name: '',
  sensitivity: MOUSE_SENSITIVITY_DEFAULT,
  fov: FOV_BASE,
  masterVolume: 0.8,
  musicVolume: 0.55,
  sfxVolume: 0.9,
  quality: 'high' as QualityPreset,
  colorblind: false,
  showFps: false,
  reducedShake: false,
});

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

function sanitize(raw: unknown): GameSettings {
  const o = (raw ?? {}) as Partial<Record<keyof GameSettings, unknown>>;
  const num = (v: unknown, fallback: number, lo: number, hi: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? clamp(v, lo, hi) : fallback;
  const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

  const quality =
    o.quality === 'low' || o.quality === 'medium' || o.quality === 'high'
      ? o.quality
      : DEFAULT_SETTINGS.quality;

  return {
    name: typeof o.name === 'string' ? o.name.slice(0, 14) : DEFAULT_SETTINGS.name,
    sensitivity: num(
      o.sensitivity,
      DEFAULT_SETTINGS.sensitivity,
      MOUSE_SENSITIVITY_DEFAULT * SENS_MULT_MIN,
      MOUSE_SENSITIVITY_DEFAULT * SENS_MULT_MAX,
    ),
    fov: num(o.fov, DEFAULT_SETTINGS.fov, FOV_MIN, FOV_UI_MAX),
    masterVolume: num(o.masterVolume, DEFAULT_SETTINGS.masterVolume, 0, 1),
    musicVolume: num(o.musicVolume, DEFAULT_SETTINGS.musicVolume, 0, 1),
    sfxVolume: num(o.sfxVolume, DEFAULT_SETTINGS.sfxVolume, 0, 1),
    quality,
    colorblind: bool(o.colorblind, DEFAULT_SETTINGS.colorblind),
    showFps: bool(o.showFps, DEFAULT_SETTINGS.showFps),
    reducedShake: bool(o.reducedShake, DEFAULT_SETTINGS.reducedShake),
  };
}

function readStorage(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return sanitize(JSON.parse(raw));
  } catch {
    // Private browsing, disabled storage, corrupt JSON — defaults are always fine.
    return { ...DEFAULT_SETTINGS };
  }
}

let state: GameSettings = readStorage();
const listeners = new Set<(s: GameSettings) => void>();

export function getSettings(): Readonly<GameSettings> {
  return state;
}

export function onSettingsChange(fn: (s: GameSettings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function saveSettings(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — the session still works, it just won't persist */
  }
}

/** Merge a patch, persist, apply document-level side effects, notify subscribers. */
export function updateSettings(patch: Partial<GameSettings>): Readonly<GameSettings> {
  state = sanitize({ ...state, ...patch });
  saveSettings();
  applySettingsToDocument();
  for (const fn of listeners) fn(state);
  return state;
}

/**
 * Push the presentation-affecting settings onto the document element so CSS can
 * react without any JS in the hot path.
 */
export function applySettingsToDocument(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.cb = state.colorblind ? 'on' : 'off';
  root.dataset.quality = state.quality;
  root.style.setProperty('--team-a', teamColor(0));
  root.style.setProperty('--team-b', teamColor(1));
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export interface SettingsPanelHooks {
  /** Raw rad/px — assign straight to `InputManager.sensitivity`. */
  onSensitivity(v: number): void;
  /** Master volume 0..1 — feed to `AudioEngine.setMasterVolume`. */
  onVolume(v: number): void;
  /** Player asked to go back to the game (in-game invocation only). */
  onResume(): void;
  /** Fired for any UI sound the host wants to make. */
  onUiSound?(kind: 'hover' | 'click' | 'back'): void;
}

/** `ParentNode` is ambiguous here — @cloudflare/workers-types declares a global one
 *  for HTMLRewriter — so this takes the structural shape it actually needs. */
type Queryable = { querySelector(selectors: string): Element | null };

const q = <T extends HTMLElement>(root: Queryable, sel: string): T => {
  const found = root.querySelector(sel);
  if (!found) throw new Error(`settings: missing element ${sel}`);
  return found as T;
};

const PANEL_HTML = `
<div class="ot-modal__scrim" data-close></div>
<div class="ot-modal__box ot-settings" role="dialog" aria-modal="true" aria-label="Settings">
  <header class="ot-settings__head">
    <h2 class="ot-modal__title">SETTINGS</h2>
    <button class="ot-btn ot-btn--icon" data-close type="button" aria-label="Close settings">&times;</button>
  </header>

  <div class="ot-settings__body">
    <section class="ot-settings__group" aria-labelledby="ot-set-aim">
      <h3 class="ot-settings__legend" id="ot-set-aim">AIM</h3>

      <label class="ot-field" for="ot-sens">
        <span class="ot-field__label">SENSITIVITY</span>
        <input class="ot-range" id="ot-sens" type="range" min="0.2" max="4" step="0.05" />
        <output class="ot-field__value" id="ot-sens-out" for="ot-sens">1.00&times;</output>
      </label>
      <label class="ot-field" for="ot-fov">
        <span class="ot-field__label">FIELD OF VIEW</span>
        <input class="ot-range" id="ot-fov" type="range" min="80" max="120" step="1" />
        <output class="ot-field__value" id="ot-fov-out" for="ot-fov">92&deg;</output>
      </label>
    </section>

    <section class="ot-settings__group" aria-labelledby="ot-set-audio">
      <h3 class="ot-settings__legend" id="ot-set-audio">AUDIO</h3>

      <label class="ot-field" for="ot-vol-master">
        <span class="ot-field__label">MASTER</span>
        <input class="ot-range" id="ot-vol-master" type="range" min="0" max="100" step="1" />
        <output class="ot-field__value" id="ot-vol-master-out" for="ot-vol-master">80</output>
      </label>

      <label class="ot-field" for="ot-vol-sfx">
        <span class="ot-field__label">SFX</span>
        <input class="ot-range" id="ot-vol-sfx" type="range" min="0" max="100" step="1" />
        <output class="ot-field__value" id="ot-vol-sfx-out" for="ot-vol-sfx">90</output>
      </label>

      <label class="ot-field" for="ot-vol-music">
        <span class="ot-field__label">MUSIC</span>
        <input class="ot-range" id="ot-vol-music" type="range" min="0" max="100" step="1" />
        <output class="ot-field__value" id="ot-vol-music-out" for="ot-vol-music">55</output>
      </label>
    </section>

    <section class="ot-settings__group" aria-labelledby="ot-set-video">
      <h3 class="ot-settings__legend" id="ot-set-video">VIDEO</h3>

      <div class="ot-field ot-field--seg">
        <span class="ot-field__label" id="ot-quality-label">QUALITY</span>
        <div class="ot-seg" role="radiogroup" aria-labelledby="ot-quality-label" id="ot-quality">
          <button class="ot-seg__opt" type="button" role="radio" data-q="low">LOW</button>
          <button class="ot-seg__opt" type="button" role="radio" data-q="medium">MED</button>
          <button class="ot-seg__opt" type="button" role="radio" data-q="high">HIGH</button>
        </div>
      </div>

      <label class="ot-switch" for="ot-cb">
        <input type="checkbox" id="ot-cb" />
        <span class="ot-switch__track"><span class="ot-switch__thumb"></span></span>
        <span class="ot-switch__label">COLOURBLIND TEAM COLOURS</span>
        <span class="ot-swatches" aria-hidden="true">
          <i class="ot-swatch" data-sw="a"></i><i class="ot-swatch" data-sw="b"></i>
        </span>
      </label>

      <label class="ot-switch" for="ot-shake">
        <input type="checkbox" id="ot-shake" />
        <span class="ot-switch__track"><span class="ot-switch__thumb"></span></span>
        <span class="ot-switch__label">REDUCE SCREEN SHAKE</span>
      </label>

      <label class="ot-switch" for="ot-fps">
        <input type="checkbox" id="ot-fps" />
        <span class="ot-switch__track"><span class="ot-switch__thumb"></span></span>
        <span class="ot-switch__label">SHOW FPS</span>
      </label>
    </section>
  </div>

  <footer class="ot-settings__foot">
    <button class="ot-btn ot-btn--ghost" type="button" id="ot-reset">RESET DEFAULTS</button>
    <button class="ot-btn ot-btn--primary" type="button" id="ot-resume">RESUME</button>
  </footer>
</div>`;

export class SettingsPanel {
  readonly element: HTMLElement;

  private readonly hooks: SettingsPanelHooks;
  private readonly sens: HTMLInputElement;
  private readonly sensOut: HTMLElement;
  private readonly fov: HTMLInputElement;
  private readonly fovOut: HTMLElement;
  private readonly volMaster: HTMLInputElement;
  private readonly volMasterOut: HTMLElement;
  private readonly volSfx: HTMLInputElement;
  private readonly volSfxOut: HTMLElement;
  private readonly volMusic: HTMLInputElement;
  private readonly volMusicOut: HTMLElement;
  private readonly quality: HTMLElement;
  private readonly cb: HTMLInputElement;
  private readonly shake: HTMLInputElement;
  private readonly fps: HTMLInputElement;
  private readonly resumeBtn: HTMLButtonElement;
  private readonly onKeyDown: (e: KeyboardEvent) => void;

  private open_ = false;
  private inGame = false;
  private lastFocus: HTMLElement | null = null;

  constructor(hooks: SettingsPanelHooks) {
    this.hooks = hooks;

    const el = document.createElement('div');
    el.className = 'ot-modal ot-settings-modal';
    el.hidden = true;
    el.innerHTML = PANEL_HTML;
    this.element = el;

    this.sens = q<HTMLInputElement>(el, '#ot-sens');
    this.sensOut = q(el, '#ot-sens-out');
    this.fov = q<HTMLInputElement>(el, '#ot-fov');
    this.fovOut = q(el, '#ot-fov-out');
    this.volMaster = q<HTMLInputElement>(el, '#ot-vol-master');
    this.volMasterOut = q(el, '#ot-vol-master-out');
    this.volSfx = q<HTMLInputElement>(el, '#ot-vol-sfx');
    this.volSfxOut = q(el, '#ot-vol-sfx-out');
    this.volMusic = q<HTMLInputElement>(el, '#ot-vol-music');
    this.volMusicOut = q(el, '#ot-vol-music-out');
    this.quality = q(el, '#ot-quality');
    this.cb = q<HTMLInputElement>(el, '#ot-cb');
    this.shake = q<HTMLInputElement>(el, '#ot-shake');
    this.fps = q<HTMLInputElement>(el, '#ot-fps');
    this.resumeBtn = q<HTMLButtonElement>(el, '#ot-resume');

    this.syncFromState();

    // --- wiring -------------------------------------------------------------
    this.sens.addEventListener('input', () => {
      const mult = Number(this.sens.value);
      const raw = MOUSE_SENSITIVITY_DEFAULT * mult;
      this.sensOut.textContent = `${mult.toFixed(2)}×`;
      updateSettings({ sensitivity: raw });
      this.hooks.onSensitivity(raw);
    });

    this.fov.addEventListener('input', () => {
      const v = Number(this.fov.value);
      this.fovOut.textContent = `${v}°`;
      updateSettings({ fov: v });
    });

    const volWire = (
      input: HTMLInputElement,
      out: HTMLElement,
      key: 'masterVolume' | 'sfxVolume' | 'musicVolume',
    ): void => {
      input.addEventListener('input', () => {
        const pct = Number(input.value);
        out.textContent = String(pct);
        updateSettings({ [key]: pct / 100 } as Partial<GameSettings>);
        if (key === 'masterVolume') this.hooks.onVolume(pct / 100);
      });
    };
    volWire(this.volMaster, this.volMasterOut, 'masterVolume');
    volWire(this.volSfx, this.volSfxOut, 'sfxVolume');
    volWire(this.volMusic, this.volMusicOut, 'musicVolume');

    this.quality.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-q]');
      if (!btn) return;
      const v = btn.dataset.q as QualityPreset;
      updateSettings({ quality: v });
      this.syncQuality();
      this.hooks.onUiSound?.('click');
    });
    // Arrow-key traversal for the radiogroup, per WAI-ARIA.
    this.quality.addEventListener('keydown', (ev) => {
      const order: QualityPreset[] = ['low', 'medium', 'high'];
      const cur = order.indexOf(state.quality);
      let next = -1;
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') next = Math.min(order.length - 1, cur + 1);
      else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') next = Math.max(0, cur - 1);
      if (next < 0) return;
      ev.preventDefault();
      updateSettings({ quality: order[next]! });
      this.syncQuality();
      this.quality.querySelector<HTMLElement>(`[data-q="${order[next]}"]`)?.focus();
    });

    this.cb.addEventListener('change', () => {
      updateSettings({ colorblind: this.cb.checked });
      this.syncSwatches();
    });
    this.shake.addEventListener('change', () => updateSettings({ reducedShake: this.shake.checked }));
    this.fps.addEventListener('change', () => updateSettings({ showFps: this.fps.checked }));

    q<HTMLButtonElement>(el, '#ot-reset').addEventListener('click', () => {
      // Keep the player's name; wiping it on a settings reset is just rude.
      updateSettings({ ...DEFAULT_SETTINGS, name: state.name });
      this.syncFromState();
      this.hooks.onSensitivity(state.sensitivity);
      this.hooks.onVolume(state.masterVolume);
      this.hooks.onUiSound?.('click');
    });

    this.resumeBtn.addEventListener('click', () => {
      this.close();
      if (this.inGame) this.hooks.onResume();
    });

    el.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.hasAttribute('data-close')) {
        this.close();
        this.hooks.onUiSound?.('back');
      }
    });

    // Focus trap + Escape, active only while the panel is open.
    this.onKeyDown = (ev: KeyboardEvent): void => {
      if (!this.open_) return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        this.close();
        if (this.inGame) this.hooks.onResume();
        return;
      }
      if (ev.key !== 'Tab') return;
      const focusables = this.focusables();
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (ev.shiftKey && (active === first || !this.element.contains(active))) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && active === last) {
        ev.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', this.onKeyDown, true);

    applySettingsToDocument();
  }

  isOpen(): boolean {
    return this.open_;
  }

  open(inGame: boolean): void {
    if (this.open_) {
      this.inGame = inGame;
      return;
    }
    this.inGame = inGame;
    this.open_ = true;
    this.lastFocus = document.activeElement as HTMLElement | null;
    this.syncFromState();
    this.resumeBtn.textContent = inGame ? 'RESUME' : 'DONE';
    this.element.hidden = false;
    // Force a reflow so the enter transition always plays from its start state.
    void this.element.offsetHeight;
    this.element.classList.add('is-open');
    this.resumeBtn.focus();
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.element.classList.remove('is-open');
    this.element.hidden = true;
    const back = this.lastFocus;
    this.lastFocus = null;
    if (back && document.contains(back)) back.focus();
  }

  toggle(inGame: boolean): void {
    if (this.open_) this.close();
    else this.open(inGame);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.element.remove();
  }

  // -------------------------------------------------------------------------

  private focusables(): HTMLElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((n) => n.offsetParent !== null || n === document.activeElement);
  }

  private syncFromState(): void {
    const mult = state.sensitivity / MOUSE_SENSITIVITY_DEFAULT;
    this.sens.value = String(mult);
    this.sensOut.textContent = `${mult.toFixed(2)}×`;

    this.fov.value = String(Math.round(state.fov));
    this.fovOut.textContent = `${Math.round(state.fov)}°`;

    const pct = (v: number): string => String(Math.round(v * 100));
    this.volMaster.value = pct(state.masterVolume);
    this.volMasterOut.textContent = pct(state.masterVolume);
    this.volSfx.value = pct(state.sfxVolume);
    this.volSfxOut.textContent = pct(state.sfxVolume);
    this.volMusic.value = pct(state.musicVolume);
    this.volMusicOut.textContent = pct(state.musicVolume);

    this.cb.checked = state.colorblind;
    this.shake.checked = state.reducedShake;
    this.fps.checked = state.showFps;

    this.syncQuality();
    this.syncSwatches();
  }

  private syncQuality(): void {
    for (const b of this.quality.querySelectorAll<HTMLElement>('[data-q]')) {
      const on = b.dataset.q === state.quality;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    }
  }

  private syncSwatches(): void {
    const a = this.element.querySelector<HTMLElement>('[data-sw="a"]');
    const b = this.element.querySelector<HTMLElement>('[data-sw="b"]');
    if (a) a.style.background = teamColor(0);
    if (b) b.style.background = teamColor(1);
  }
}
